import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  LayoutDashboard, Image, Palette, CalendarDays, Ticket,
  Users, ShoppingBag, Settings, ExternalLink, LogOut, PanelLeftClose, PanelLeftOpen, Tag, UserCircle, MessageSquare, Inbox, Newspaper, MailPlus, HelpCircle, Zap, Handshake, FileText,
} from 'lucide-react';
import { useAdminStore } from '@/lib/admin-store';
import { adminApi } from '@/lib/api';

const NAV_ITEMS = [
  { path: '/admin', label: 'Dashboard', icon: LayoutDashboard, end: true },
  { path: '/admin/artworks', label: 'Artworks', icon: Image },
  { path: '/admin/categories', label: 'Categories', icon: Tag },
  { path: '/admin/artists', label: 'Artists', icon: Palette },
  { path: '/admin/exhibitions', label: 'Exhibitions', icon: CalendarDays },
  { path: '/admin/art-fairs', label: 'Art Fairs', icon: Ticket },
  { path: '/admin/dropshop', label: 'Dropshop', icon: Zap },
  { path: '/admin/team', label: 'Team', icon: Users },
  { path: '/admin/orders', label: 'Orders', icon: ShoppingBag },
  { path: '/admin/reviews', label: 'Reviews', icon: MessageSquare },
  { path: '/admin/inbox', label: 'Inbox', icon: Inbox },
  { path: '/admin/subscribers', label: 'Subscribers', icon: MailPlus },
  { path: '/admin/news', label: 'News', icon: Newspaper },
  { path: '/admin/faq', label: 'FAQ', icon: HelpCircle },
  { path: '/admin/partners', label: 'Partners', icon: Handshake },
  { path: '/admin/users', label: 'Users', icon: UserCircle },
  { path: '/admin/settings', label: 'Settings', icon: Settings },
];

const PAGE_ITEMS = [
  { path: '/admin/pages/about', label: 'About' },
  { path: '/admin/pages/terms', label: 'Terms' },
  { path: '/admin/pages/privacy', label: 'Privacy' },
  { path: '/admin/pages/cookie', label: 'Cookie' },
  { path: '/admin/pages/careers', label: 'Careers' },
  { path: '/admin/pages/shipping', label: 'Shipping' },
];

interface Props {
  collapsed: boolean;
  onToggle: () => void;
}

const AdminSidebar: React.FC<Props> = ({ collapsed, onToggle }) => {
  const location = useLocation();
  const pendingOrders = useAdminStore((s) => s.orders.filter((o) => o.status === 'pending').length);
  const openConversations = useAdminStore((s) => s.conversations.filter((c) => c.status === 'open').length);
  const subscriberCount = useAdminStore((s) => s.subscribers.length);
  const newsCount = useAdminStore((s) => s.newsArticles.filter((a) => a.isPublished).length);
  const [userCount, setUserCount] = React.useState(0);
  React.useEffect(() => {
    adminApi.getUsers().then((d) => setUserCount((d as unknown[]).length)).catch(() => {});
  }, []);
  const logout = useAdminStore((s) => s.logout);

  const isActive = (path: string, end?: boolean): boolean => {
    if (end) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  return (
    <motion.aside
      animate={{ width: collapsed ? 64 : 240 }}
      transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
      className="fixed left-0 top-0 h-screen bg-white border-r border-black/[0.04] z-[200] flex flex-col overflow-hidden"
    >
      {/* Logo */}
      <div className="flex items-center h-16 px-4 border-b border-black/[0.04] shrink-0">
        {!collapsed && (
          <motion.span
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-lg font-syncopate font-bold tracking-tighter"
          >
            FOTOA.
          </motion.span>
        )}
        <button
          onClick={onToggle}
          className={`${collapsed ? 'mx-auto' : 'ml-auto'} w-8 h-8 rounded-lg flex items-center justify-center text-black/30 hover:text-black hover:bg-black/[0.04] transition-all`}
        >
          {collapsed ? <PanelLeftOpen size={16} /> : <PanelLeftClose size={16} />}
        </button>
      </div>

      {/* Nav */}
      <nav className="flex-1 py-3 px-2 space-y-0.5 overflow-y-auto">
        {NAV_ITEMS.map((item) => {
          const active = isActive(item.path, item.end);
          return (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.end}
              className="relative flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors group"
            >
              {active && (
                <motion.div
                  layoutId="admin-nav-active"
                  className="absolute inset-0 bg-black rounded-xl"
                  transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                />
              )}
              <item.icon
                size={18}
                className={`relative z-10 shrink-0 transition-colors ${
                  active
                    ? 'text-white'
                    : 'text-black/30 group-hover:text-black/60'
                }`}
              />
              {!collapsed && (
                <span
                  className={`relative z-10 text-[11px] font-bold uppercase tracking-[0.08em] transition-colors whitespace-nowrap ${
                    active
                      ? 'text-white'
                      : 'text-black/40 group-hover:text-black/70'
                  }`}
                >
                  {item.label}
                </span>
              )}
              {item.label === 'Orders' && pendingOrders > 0 && (
                <span className={`relative z-10 ${collapsed ? '' : 'ml-auto'} w-5 h-5 rounded-full text-[8px] font-bold flex items-center justify-center ${
                  active ? 'bg-white/20 text-white' : 'bg-black text-white'
                }`}>
                  {pendingOrders}
                </span>
              )}
              {item.label === 'Inbox' && openConversations > 0 && (
                <span className={`relative z-10 ${collapsed ? '' : 'ml-auto'} w-5 h-5 rounded-full text-[8px] font-bold flex items-center justify-center ${
                  active ? 'bg-white/20 text-white' : 'bg-black text-white'
                }`}>
                  {openConversations}
                </span>
              )}
              {item.label === 'Subscribers' && subscriberCount > 0 && (
                <span className={`relative z-10 ${collapsed ? '' : 'ml-auto'} w-5 h-5 rounded-full text-[8px] font-bold flex items-center justify-center ${
                  active ? 'bg-white/20 text-white' : 'bg-black/10 text-black/40'
                }`}>
                  {subscriberCount}
                </span>
              )}
              {item.label === 'News' && newsCount > 0 && (
                <span className={`relative z-10 ${collapsed ? '' : 'ml-auto'} w-5 h-5 rounded-full text-[8px] font-bold flex items-center justify-center ${
                  active ? 'bg-white/20 text-white' : 'bg-black/10 text-black/40'
                }`}>
                  {newsCount}
                </span>
              )}
              {item.label === 'Users' && userCount > 0 && (
                <span className={`relative z-10 ${collapsed ? '' : 'ml-auto'} w-5 h-5 rounded-full text-[8px] font-bold flex items-center justify-center ${
                  active ? 'bg-white/20 text-white' : 'bg-black/10 text-black/40'
                }`}>
                  {userCount}
                </span>
              )}
            </NavLink>
          );
        })}

        {/* Pages section */}
        {!collapsed && (
          <div className="mt-2 pt-2 border-t border-black/[0.04]">
            <p className="px-3 py-1.5 text-[8px] font-bold uppercase tracking-[0.2em] text-black/20">Pages</p>
            {PAGE_ITEMS.map((item) => {
              const active = isActive(item.path);
              return (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className="relative flex items-center gap-2.5 px-3 py-2 rounded-xl transition-colors group"
                >
                  {active && (
                    <motion.div layoutId="admin-nav-active" className="absolute inset-0 bg-black rounded-xl" transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }} />
                  )}
                  <FileText size={14} className={`relative z-10 shrink-0 transition-colors ${active ? 'text-white' : 'text-black/25 group-hover:text-black/50'}`} />
                  <span className={`relative z-10 text-[10px] font-bold uppercase tracking-[0.06em] transition-colors whitespace-nowrap ${active ? 'text-white' : 'text-black/35 group-hover:text-black/60'}`}>
                    {item.label}
                  </span>
                </NavLink>
              );
            })}
          </div>
        )}
      </nav>

      {/* Bottom */}
      <div className="border-t border-black/[0.04] py-3 px-2 space-y-0.5 shrink-0">
        <a
          href="/"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-black/30 hover:text-black/60 hover:bg-black/[0.03] transition-all"
        >
          <ExternalLink size={18} className="shrink-0" />
          {!collapsed && (
            <span className="text-[11px] font-bold uppercase tracking-[0.08em] whitespace-nowrap">View Site</span>
          )}
        </a>
        <button
          onClick={logout}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-black/30 hover:text-red-500 hover:bg-red-500/5 transition-all"
        >
          <LogOut size={18} className="shrink-0" />
          {!collapsed && (
            <span className="text-[11px] font-bold uppercase tracking-[0.08em] whitespace-nowrap">Logout</span>
          )}
        </button>
      </div>
    </motion.aside>
  );
};

export default AdminSidebar;
